import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, Optional, Output, Self, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NgControl, ReactiveFormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Subject } from 'rxjs';


/** Data structure for holding telephone number. */
export class MyTime {
    constructor(
        public hours: number,
        public minutes: number,
    ) { }
}

@Component({
    selector: 'app-time',
    templateUrl: './time.component.html',
    styleUrls: ['./time.component.scss'],
    providers: [{ provide: MatFormFieldControl, useExisting: TimeComponent }],
    host: {
        '[class.example-floating]': 'shouldLabelFloat',
        '[id]': 'id',
        '[attr.aria-describedby]': 'describedBy',
    },
    standalone: true,
    imports: [
        ReactiveFormsModule, MatOptionModule, MatSelectModule, DecimalPipe, CommonModule
    ]
})
export class TimeComponent implements ControlValueAccessor, MatFormFieldControl<MyTime>, OnDestroy {

    @Output()
    onBlur: EventEmitter<true> = new EventEmitter()
    static nextId = 0;
    @ViewChild('hours') hoursInput!: HTMLInputElement;
    @ViewChild('minutes') minutesInput!: HTMLInputElement;


    parts!: FormGroup;
    stateChanges = new Subject<void>();
    focused = false;
    touched = false;
    controlType = 'example-tel-input';
    id = `app-time-input-${TimeComponent.nextId++}`;
    onChange = (_: any) => { };
    onTouched = () => { };


    get empty() {
        const {
            value: { hours, minutes }
        } = this.parts;

        return !hours && !minutes;
    }

    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    @Input('aria-describedby') userAriaDescribedBy!: string;

    @Input()
    get placeholder(): string {
        return this._placeholder;
    }
    set placeholder(value: string) {
        this._placeholder = value;
        this.stateChanges.next();
    }
    private _placeholder!: string;

    @Input()
    get required(): boolean {
        return this._required;
    }
    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }
    private _required = false;

    @Input()
    get disabled(): boolean {
        return this._disabled;
    }
    set disabled(value: boolean) {
        this._disabled = coerceBooleanProperty(value);
        this._disabled ? this.parts.disable() : this.parts.enable();
        this.stateChanges.next();
    }
    private _disabled = false;

    @Input()
    get value(): MyTime | null {

        const {
            value: { hours, minutes }
        } = this.parts;
        return new MyTime(hours, minutes);

        // if (this.parts.valid) {
        //   const {
        //     value: { hours, minutes }
        //   } = this.parts;
        //   return new MyTime(hours, minutes);
        // }
        // return null;
    }
    set value(tel: MyTime | null) {
        const { hours, minutes } = tel || new MyTime(0, 0);
        this.parts.setValue({ hours, minutes });
        this.stateChanges.next();
    }

    get errorState(): boolean {
        return this.parts.invalid && this.touched;
        //return false;
    }

    mins: number[] = []
    hoursNum: number[] = []

    constructor(
        formBuilder: FormBuilder,
        private _focusMonitor: FocusMonitor,
        private _elementRef: ElementRef<HTMLElement>,

        @Optional() @Self() public ngControl: NgControl) {
        for (let x = 0; x < 60; x++) {
            this.mins.push(x)
        }
        for (let x = 0; x < 24; x++) {
            this.hoursNum.push(x)
        }

        this.parts = formBuilder.group({
            hours: [
                null,
                []
            ],
            minutes: [
                null,
                []
            ]
        });

        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnDestroy() {
        this.stateChanges.complete();
        this._focusMonitor.stopMonitoring(this._elementRef);
    }

    onFocusIn(event: FocusEvent) {
        if (!this.focused) {
            this.focused = true;
            this.stateChanges.next();
        }
    }

    onFocusOut(event: FocusEvent) {
        if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
            this.touched = true;
            this.focused = false;
            this.onTouched();
            this.stateChanges.next();
        }
    }

    autoFocusNext(control: AbstractControl, nextElement?: HTMLInputElement): void {
        if (!control.errors && nextElement) {
            this._focusMonitor.focusVia(nextElement, 'program');
        }
    }

    autoFocusPrev(control: AbstractControl, prevElement: HTMLInputElement): void {
        if (control.value.length < 1) {
            this._focusMonitor.focusVia(prevElement, 'program');
        }
    }

    setDescribedByIds(ids: string[]) {
        const controlElement = this._elementRef.nativeElement
            .querySelector('.example-tel-input-container')!;
        controlElement.setAttribute('aria-describedby', ids.join(' '));
    }

    onContainerClick() {
        if (this.parts.controls['hours'].valid) {
            this._focusMonitor.focusVia(this.hoursInput, 'program');
        } else {
            this._focusMonitor.focusVia(this.minutesInput, 'program');
        }
    }

    writeValue(tel: MyTime | null): void {
        this.value = tel;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }


    _handleInput(): void {

        this.onChange(this.value);
    }

    _doBlur() {
        this.onBlur.next(true)

    }

    static ngAcceptInputType_disabled: BooleanInput;
    static ngAcceptInputType_required: BooleanInput;
}

